import React, { useCallback, useMemo, useRef, useState } from 'react';
import SourcesPanel from './SourcesPanel';

// Only links the panel knows how to open; anything else is a title-only row.
const safeLink = (link) => {
  if (!link) return null;
  if (link.type === 'external' && typeof link.url === 'string' && /^https?:\/\//i.test(link.url)) {
    return { type: 'external', url: link.url };
  }
  if (link.type === 'resource' && link.resourceId) {
    return { type: 'resource', resourceId: String(link.resourceId) };
  }
  return null;
};

/**
 * One row per source document. Citations are per passage, and old
 * conversations store several KB editions of one document that the backend
 * maps to the same title and link — those count once.
 */
export const uniqueSources = (citations) => {
  if (!Array.isArray(citations)) return [];
  const seen = new Set();
  const sources = [];
  for (const citation of citations) {
    // title/label/link come from the backend (lib/kbSources.js); the raw
    // source name is only a fallback for an undecorated response.
    const title = String((citation && (citation.title || citation.source)) || '').trim();
    if (!title) continue;
    const link = safeLink(citation.link);
    const key = JSON.stringify([title, link && link.type, link && (link.url || link.resourceId)]);
    if (seen.has(key)) continue;
    seen.add(key);
    sources.push({ key, title, label: citation.label || null, link });
  }
  return sources;
};

/**
 * "Vis kilder (n)" under an AI answer; opens the sources panel. Renders
 * nothing when the answer has no sources.
 */
const MessageSources = ({ citations }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const sources = useMemo(() => uniqueSources(citations), [citations]);
  const close = useCallback(() => setOpen(false), []);

  if (sources.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="mt-2 -ml-3 inline-flex min-h-[48px] items-center gap-2 rounded-lg px-3 text-sm font-medium text-text-secondary transition-colors hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        Vis kilder ({sources.length})
      </button>
      {open && <SourcesPanel sources={sources} onClose={close} returnFocusRef={buttonRef} />}
    </>
  );
};

export default MessageSources;
